'use client';

import { c as _c } from "react/compiler-runtime";
import { ProductCard as BaseProductCard, QuantitySelector } from '@sprd/sprd-component-kit/v2';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useIntersectionObserver } from 'usehooks-ts';
import { Assortment } from '@/utility/shopIds';
import { DiscountedPrice } from '../Price/DiscountedPrice';
import { PriceProps } from '../Price/Price';
import { SprdResponsiveImage, SprdResponsiveImageData } from '../ResponsiveImage/SprdResponsiveImage';
export type VolumeDiscountProductItem = Omit<VolumeDiscountProductCardProps, 'index'> & {
  printTypeIds: string[];
  appearanceIds: string[];
  isEmbroideryOnly?: boolean;
  /** Volume discount tiers for this product type; the tier is picked by this product's quantity. */
  discounts: ProductTypeDiscount[];
};
export type VolumeDiscountProductCardProps = {
  index: number;
  price: number;
  currentDiscountAmount?: number;
  className?: string;
  title: string;
  imageBaseUrl: string;
  productTypeId: string;
  // which shop the product type belongs to, for its strike price (default: marketplace)
  assortment?: Assortment;
  priceFormat?: PriceProps['format'];
  image: SprdResponsiveImageData;
  selectedIndex?: number;
  quantity: number;
  onItemSelect?: (index: number) => void;
  onQuantityChange?: (index: number, quantity: number) => void;
};
const VolumeDiscountProductCard: React.FC<VolumeDiscountProductCardProps> = t0 => {
  const $ = _c(59);
  const {
    index,
    imageBaseUrl,
    image,
    title,
    price,
    currentDiscountAmount,
    className,
    productTypeId,
    assortment: t1,
    priceFormat,
    selectedIndex,
    quantity,
    onItemSelect,
    onQuantityChange
  } = t0;
  const assortment = t1 === undefined ? "marketplace" : t1;
  const t = useTranslations("volumeDiscountCalculator.card");
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      freezeOnceVisible: true,
      rootMargin: "200px"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    ref: observeCard,
    isIntersecting
  } = useIntersectionObserver(t2);
  const t3 = selectedIndex === index && "border-black!";
  let t4;
  if ($[1] !== t3) {
    t4 = clsx("flex h-full shrink-0 cursor-pointer flex-col overflow-hidden rounded border border-neutral-200 text-left transition-colors hover:border-neutral-800", t3);
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = selectedIndex === index;
  let t6;
  let t7;
  if ($[3] !== index || $[4] !== onItemSelect) {
    t6 = () => {
      onItemSelect?.(index);
    };
    t7 = e => {
      if (e.target !== e.currentTarget) {
        return;
      }
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onItemSelect?.(index);
      }
    };
    $[3] = index;
    $[4] = onItemSelect;
    $[5] = t6;
    $[6] = t7;
  } else {
    t6 = $[5];
    t7 = $[6];
  }
  let t8;
  if ($[7] !== isIntersecting || $[8] !== t) {
    t8 = !isIntersecting ? {
      visible: false
    } : {
      visible: true,
      nextLabel: t("a11y.nextImage"),
      prevLabel: t("a11y.previousImage")
    };
    $[7] = isIntersecting;
    $[8] = t;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== image || $[11] !== imageBaseUrl) {
    t9 = <SprdResponsiveImage className="m-auto w-fit" imageBaseUrl={imageBaseUrl} image={image} />;
    $[10] = image;
    $[11] = imageBaseUrl;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t8 || $[14] !== t9) {
    t10 = <BaseProductCard.Media className="aspect-1 flex h-27 w-auto items-center justify-center bg-neutral-100 sm:h-34.5" navigation={t8}>{t9}</BaseProductCard.Media>;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== title) {
    t11 = <p className="text-xs leading-tight font-medium text-black">{title}</p>;
    $[16] = title;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const t12 = Number(price);
  let t13;
  if ($[18] !== assortment || $[19] !== currentDiscountAmount || $[20] !== priceFormat || $[21] !== productTypeId || $[22] !== t12) {
    t13 = <div className="text-xs [&_.line-through]:text-neutral-800 [&_.text-red-600]:font-bold"><DiscountedPrice price={t12} productTypeId={productTypeId} format={priceFormat} currentDiscountAmount={currentDiscountAmount} excludePromoDiscount={true} assortment={assortment} /></div>;
    $[18] = assortment;
    $[19] = currentDiscountAmount;
    $[20] = priceFormat;
    $[21] = productTypeId;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t11 || $[25] !== t13) {
    t14 = <div className="flex flex-col gap-1 bg-white px-3 pt-2 pb-2.5">{t11}{t13}</div>;
    $[24] = t11;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== className || $[28] !== observeCard || $[29] !== t10 || $[30] !== t14) {
    t15 = <BaseProductCard.Root ref={observeCard} className={className}>{t10}{t14}</BaseProductCard.Root>;
    $[27] = className;
    $[28] = observeCard;
    $[29] = t10;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t15 || $[33] !== t5 || $[34] !== t6 || $[35] !== t7) {
    t16 = <div className="grow" role="button" tabIndex={0} aria-pressed={t5} onClick={t6} onKeyDown={t7}>{t15}</div>;
    $[32] = t15;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== index || $[38] !== onQuantityChange) {
    t17 = value => onQuantityChange?.(index, value);
    $[37] = index;
    $[38] = onQuantityChange;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      root: "w-full border-x-0 border-b-0",
      input: "w-full",
      button: "h-9 w-9 border-none"
    };
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t) {
    t19 = t("a11y.quantityInputLabel");
    $[41] = t;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t) {
    t20 = t("a11y.decreaseQuantity");
    $[43] = t;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t) {
    t21 = t("a11y.increaseQuantity");
    $[45] = t;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t19 || $[48] !== t20 || $[49] !== t21) {
    t22 = {
      input: t19,
      decreaseButton: t20,
      increaseButton: t21
    };
    $[47] = t19;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== quantity || $[52] !== t17 || $[53] !== t22) {
    t23 = <QuantitySelector type="number" min={0} step={1} max={999} quantity={quantity} onChange={t17} onFocus={_temp} onInput={_temp2} className={t18} labels={t22} />;
    $[51] = quantity;
    $[52] = t17;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t16 || $[56] !== t23 || $[57] !== t4) {
    t24 = <div className={t4}>{t16}{t23}</div>;
    $[55] = t16;
    $[56] = t23;
    $[57] = t4;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  return t24;
};
export default VolumeDiscountProductCard;
function _temp(e_0) {
  return e_0.target.select();
}
function _temp2(e_1) {
  const input = e_1.currentTarget;
  if (/^0\d/.test(input.value)) {
    input.value = String(Number(input.value));
  }
}